import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, ViewChild, inject } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-video-view-modal',
  standalone: true,
  imports: [CommonModule, TranslocoPipe],
  templateUrl: './video-view-modal.component.html',
  styleUrl: './video-view-modal.component.scss'
})
export class VideoViewModalComponent {
  private readonly transloco = inject(TranslocoService);

  @Input() title = this.transloco.translate('compliance.shared.modals.videoView.title');
  @Input() videoName = '';

  @Input() videoUrl: string | null = null;

  @Input() downloadHandler?: () => Promise<string | null>;

  @ViewChild('videoEl') private videoEl?: ElementRef<HTMLVideoElement>;
  @ViewChild('playerEl') private playerEl?: ElementRef<HTMLElement>;

  isDownloading = false;
  isPlaying = false;
  isMuted = false;
  currentTime = 0;
  duration = 0;

  public activeModal = inject(NgbActiveModal);

  private get video(): HTMLVideoElement | null {
    return this.videoEl?.nativeElement ?? null;
  }

  get progressPercent(): number {
    return this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;
  }

  togglePlay(): void {
    const video = this.video;
    if (!video) {
      return;
    }
    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  }

  toggleMute(): void {
    const video = this.video;
    if (!video) {
      return;
    }
    video.muted = !video.muted;
    this.isMuted = video.muted;
  }

  toggleFullscreen(): void {
    const player = this.playerEl?.nativeElement;
    if (!player) {
      return;
    }
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void player.requestFullscreen();
    }
  }

  onSeek(event: Event): void {
    const video = this.video;
    if (!video || !this.duration) {
      return;
    }
    const value = Number((event.target as HTMLInputElement).value);
    video.currentTime = (value / 100) * this.duration;
  }

  onPlay(): void {
    this.isPlaying = true;
  }

  onPause(): void {
    this.isPlaying = false;
  }

  onLoadedMetadata(): void {
    this.duration = this.video?.duration ?? 0;
    this.isMuted = this.video?.muted ?? false;
  }

  onTimeUpdate(): void {
    this.currentTime = this.video?.currentTime ?? 0;
  }

  formatTime(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) {
      return '0:00';
    }
    const total = Math.floor(seconds);
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  closeModal(): void {
    this.activeModal.dismiss();
  }

  async onDownload(): Promise<void> {
    if (this.isDownloading || !this.downloadHandler) {
      return;
    }

    this.isDownloading = true;
    try {
      const url = await this.downloadHandler();
      if (!url) {
        return;
      }

      const a = document.createElement('a');
      a.href = url;
      a.download = this.videoName || 'video';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      this.isDownloading = false;
    }
  }
}
